// src/components/forms/SessionForm.jsx
import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  BookOpen, 
  Users, 
  School,
  Briefcase,
  Building,
  MapPin,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  GraduationCap,
  Target,
  Loader2,
  Plus,
  Minus,
  Upload,
  FileText,
  Video,
  Youtube,
  Download,
  Trash2,
  File,
  ExternalLink
} from 'lucide-react';
import { SessionService } from '../../services/sessionService';
import { useAuth } from '../../contexts/AuthContext';

const SessionForm = ({ onSubmit, initialData = null, onCancel }) => {
  const { userProfile, userContexts, activeContext } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [sessionLearners, setSessionLearners] = useState([]);
  const [loadingLearners, setLoadingLearners] = useState(false);

  const [formData, setFormData] = useState({
    // Session Context
    sessionContext: initialData?.sessionContext || 'School Session',
    contextId: initialData?.contextId || activeContext?.id || '',
    
    // Session Basic Info
    sessionTitle: initialData?.sessionTitle || '',
    sessionDate: initialData?.sessionDate 
      ? new Date(initialData.sessionDate).toISOString().split('T')[0] 
      : new Date().toISOString().split('T')[0],
    sessionTime: initialData?.sessionTime || '',
    duration: initialData?.duration || 60,
    
    // Session Type
    sessionCategory: initialData?.sessionCategory || 'Lesson',
    sessionType: initialData?.sessionType || 'Class Lesson',
    
    // Academic Details
    subject: initialData?.subject || userProfile?.subjects?.[0] || '',
    grades: initialData?.grades || [userProfile?.grades?.[0] || ''],
    
    // Content
    topics: initialData?.topics || [''],
    subtopics: initialData?.subtopics || [''],
    learningObjectives: initialData?.learningObjectives || [''],
    
    // Session Details
    venue: initialData?.venue || '',
    notes: initialData?.notes || '',
    
    // Enhanced Features
    lessonPlan: initialData?.lessonPlan || null,
    lessonPlanUrl: initialData?.lessonPlanUrl || null,
    lessonPlanPath: initialData?.lessonPlanPath || null,
    resources: initialData?.resources || []
  });

  const sessionCategories = {
    'Lesson': [
      'Class Lesson',
      'Practical Lesson', 
      'Tutorial',
      'Seminar',
      'Online Lesson'
    ],
    'Assessment': [
      'Class Test',
      'Examination',
      'Practical Assessment'
    ]
  };

  // Get available contexts
  const getAvailableContexts = () => {
    if (formData.sessionContext === 'School Session') {
      return userContexts?.filter(ctx => ctx.type === 'institution') || [];
    } else if (formData.sessionContext === 'Project Session') {
      return userContexts?.filter(ctx => ctx.type === 'project') || [];
    }
    return [];
  };

  const getSelectedContext = () => {
    return userContexts?.find(ctx => ctx.id === formData.contextId);
  };

  const getSessionTypeIcon = () => {
    switch (formData.sessionContext) {
      case 'School Session':
        return <School className="w-5 h-5 text-blue-600" />;
      case 'Project Session':
        return <Briefcase className="w-5 h-5 text-green-600" />;
      default:
        return <Building className="w-5 h-5 text-gray-600" />;
    }
  };

  useEffect(() => {
    console.log('🔍 SessionForm Debug:', {
      userContexts,
      availableContexts: getAvailableContexts(),
      selectedContext: getSelectedContext(),
      sessionContext: formData.sessionContext
    });
  }, [userContexts, formData.sessionContext, formData.contextId]);

  // Load session learners when context, grades, or subject changes
  useEffect(() => {
    if (formData.contextId && formData.grades.length > 0 && formData.subject) {
      loadSessionLearners();
    } else {
      setSessionLearners([]);
    }
  }, [formData.contextId, formData.grades, formData.subject]);

  // Replace the loadSessionLearners function (around line 81)
  const loadSessionLearners = async () => {
    try {
      setLoadingLearners(true);
      setError(null);
      
      // Get the selected context (school or project)
      const selectedContext = getSelectedContext();
      
      if (!selectedContext) {
        setError('Please select a school or project first');
        return;
      }
      
      console.log('Loading learners for context:', selectedContext);
      
      // Use context-aware method that handles both institution and project
      const learners = await SessionService.getSessionLearnersByContext(
        selectedContext.name,
        selectedContext.type, // 'institution' or 'project'
        formData.grades,
        formData.subject
      );
      
      console.log('Loaded learners:', learners);
      setSessionLearners(learners);
      
    } catch (err) {
      console.error('Error loading session learners:', err);
      setError('Failed to load learners for this session: ' + err.message);
    } finally {
      setLoadingLearners(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'sessionContext') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        contextId: ''
      }));
    } else if (name === 'sessionCategory') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        sessionType: sessionCategories[value]?.[0] || ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Array field handlers
  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  // Lesson Plan Upload
  const handleLessonPlanUpload = async (file) => {
    if (!file) return;

    // Validate file type and size
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      setError('Lesson plan must be a PDF, Word document, or text file');
      return;
    }

    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }

    try {
      setUploading(true);
      const sessionId = initialData?.id || 'temp-' + Date.now();
      const uploadResult = await SessionService.uploadLessonPlan(file, sessionId);
      
      setFormData(prev => ({
        ...prev,
        lessonPlan: file,
        lessonPlanUrl: uploadResult.url,
        lessonPlanPath: uploadResult.path
      }));
    } catch (err) {
      console.error('Lesson plan upload error:', err);
      setError('Failed to upload lesson plan: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLessonPlan = async () => {
    if (formData.lessonPlanPath) {
      try {
        await SessionService.deleteFile(formData.lessonPlanPath);
      } catch (err) {
        console.error('Error deleting lesson plan:', err);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      lessonPlan: null,
      lessonPlanUrl: null,
      lessonPlanPath: null
    }));
  };

  // Enhanced Resource Management
  const addResource = (type = 'text') => {
    const newResource = {
      id: Date.now() + Math.random(),
      type, // 'text', 'file', 'youtube'
      name: '',
      value: '', // text content, file URL, or YouTube URL
      fileName: '',
      filePath: ''
    };
    
    setFormData(prev => ({
      ...prev,
      resources: [...prev.resources, newResource]
    }));
  };

  const removeResource = async (resourceId) => {
    const resource = formData.resources.find(r => r.id === resourceId);
    
    // Delete file from storage if it's a file resource
    if (resource?.type === 'file' && resource.filePath) {
      try {
        await SessionService.deleteFile(resource.filePath);
      } catch (err) {
        console.error('Error deleting resource file:', err);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.filter(r => r.id !== resourceId)
    }));
  };

  const updateResource = (resourceId, updates) => {
    setFormData(prev => ({
      ...prev,
      resources: prev.resources.map(resource =>
        resource.id === resourceId ? { ...resource, ...updates } : resource
      )
    }));
  };

  const handleResourceFileUpload = async (file, resourceId) => {
    if (!file) return;

    // Validate file type and size
    const allowedTypes = [
      // Documents
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-powerpoint',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'text/plain',
      'text/csv',
      // Images
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      // Videos
      'video/mp4',
      'video/avi',
      'video/mov',
      'video/wmv',
      'video/webm'
    ];
    const maxSize = 50 * 1024 * 1024; // 50MB

    if (!allowedTypes.includes(file.type)) {
      setError('File type not allowed. Please upload documents, images, or videos.');
      return;
    }

    if (file.size > maxSize) {
      setError('File size must be less than 50MB');
      return;
    }

    try {
      setUploading(true);
      const sessionId = initialData?.id || 'temp-' + Date.now();
      const uploadResult = await SessionService.uploadResourceFile(file, sessionId, resourceId);
      
      updateResource(resourceId, {
        value: uploadResult.url,
        fileName: file.name,
        filePath: uploadResult.path
      });
    } catch (err) {
      console.error('Resource file upload error:', err);
      setError('Failed to upload resource file: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // YouTube URL validation
  const validateYouTubeUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    return youtubeRegex.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.sessionTitle.trim()) {
      setError('Session title is required');
      return;
    }

    if (!formData.contextId) {
      setError('Please select a school or project');
      return;
    }

    if (!formData.subject) {
      setError('Please select a subject');
      return;
    }

    // Validate YouTube URLs
    for (const resource of formData.resources) {
      if (resource.type === 'youtube' && resource.value && !validateYouTubeUrl(resource.value)) {
        setError(`Invalid YouTube URL: ${resource.name || 'Unnamed resource'}`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const selectedContext = getSelectedContext();
      const sessionData = {
        ...formData,
        context: selectedContext ? {
          id: selectedContext.id,
          type: selectedContext.type,
          name: selectedContext.name,
          institutionType: selectedContext.institutionType,
          location: selectedContext.location
        } : null,
        grades: formData.grades.filter(g => g.trim()),
        topics: formData.topics.filter(t => t.trim()),
        subtopics: formData.subtopics.filter(s => s.trim()),
        learningObjectives: formData.learningObjectives.filter(o => o.trim()),
        resources: formData.resources.filter(r => {
          if (r.type === 'text') return r.name.trim() && r.value.trim();
          if (r.type === 'file') return r.fileName && r.value;
          if (r.type === 'youtube') return r.name.trim() && r.value.trim();
          return false;
        }),
        sessionLearners: sessionLearners.map(learner => ({
          id: learner.id,
          learnerSurname: learner.learnerSurname || learner.surname,
          learnerName: learner.learnerName || learner.firstName,
          grade: learner.grade,
          school: learner.school || learner.schoolName
        })),
        totalLearners: sessionLearners.length
      };

      if (initialData?.id) {
        await SessionService.updateSession(initialData.id, sessionData);
      } else {
        await SessionService.createSession(sessionData);
      }

      setSuccess(true);
      setTimeout(() => {
        onSubmit && onSubmit();
      }, 1500);

    } catch (err) {
      console.error('Error saving session:', err);
      setError(err.message || 'Failed to save session');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-4xl mx-auto p-4 bg-white rounded-lg shadow-lg">
        <div className="text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Session {initialData?.id ? 'Updated' : 'Created'} Successfully!
          </h3>
          <p className="text-gray-600">
            {formData.sessionTitle} has been saved.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-gray-900">
            {initialData?.id ? 'Edit Session' : 'Create New Session'}
          </h2>
        </div>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Session Context */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            {getSessionTypeIcon()}
            Session Context
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Context *
              </label>
              <select
                name="sessionContext"
                value={formData.sessionContext}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="School Session">School Session</option>
                <option value="Project Session">Project Session</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {formData.sessionContext === 'School Session' ? 'School' : 'Project'} *
              </label>
              <select
                name="contextId"
                value={formData.contextId}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select {formData.sessionContext === 'School Session' ? 'school' : 'project'}</option>
                {getAvailableContexts().map(context => (
                  <option key={context.id} value={context.id}>
                    {context.name} {context.location && `(${context.location})`}
                  </option>
                ))}
              </select>
              {getAvailableContexts().length === 0 && (
                <p className="text-sm text-gray-500 mt-1">
                  No {formData.sessionContext === 'School Session' ? 'schools' : 'projects'} available. Please add one in your profile settings.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Basic Session Info */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Basic Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Title *
              </label>
              <input
                type="text"
                name="sessionTitle"
                value={formData.sessionTitle}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter session title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Date *
              </label>
              <input
                type="date"
                name="sessionDate"
                value={formData.sessionDate}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Time
              </label>
              <input
                type="time"
                name="sessionTime"
                value={formData.sessionTime}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (minutes)
              </label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Venue
              </label>
              <input
                type="text"
                name="venue"
                value={formData.venue}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Classroom, Lab, etc."
              />
            </div>
          </div>
        </div>

        {/* Session Type */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Session Type
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category *
              </label>
              <select
                name="sessionCategory"
                value={formData.sessionCategory}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="Lesson">Lesson</option>
                <option value="Assessment">Assessment</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type *
              </label>
              <select
                name="sessionType"
                value={formData.sessionType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                {sessionCategories[formData.sessionCategory]?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Academic Details */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-blue-600" />
            Academic Details
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select subject</option>
                {userProfile?.subjects?.map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Grades * (Multi-grade supported)
              </label>
              <div className="space-y-2">
                {formData.grades.map((grade, index) => (
                  <div key={index} className="flex gap-2">
                    <select
                      value={grade}
                      onChange={(e) => updateArrayItem('grades', index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select grade</option>
                      {userProfile?.grades?.map(g => (
                        <option key={g} value={g}>Grade {g}</option>
                      ))}
                    </select>
                    {formData.grades.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('grades', index)}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('grades')}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Grade
                </button>
              </div>
            </div>
          </div>

          {/* Session Learners Preview */}
          {loadingLearners && (
            <div className="mt-4 p-3 bg-blue-50 rounded-md">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                <span className="text-blue-800 text-sm">Loading learners for this session...</span>
              </div>
            </div>
          )}

          {!loadingLearners && sessionLearners.length > 0 && (
            <div className="mt-4 p-3 bg-green-50 rounded-md">
              <div className="flex items-center gap-2 text-green-800">
                <Users className="w-4 h-4" />
                <span className="text-sm font-medium">
                  {sessionLearners.length} learner(s) will be included in this session
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Lesson Plan Upload */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Lesson Plan
          </h3>
          
          {!formData.lessonPlan && !formData.lessonPlanUrl ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={(e) => handleLessonPlanUpload(e.target.files[0])}
                className="hidden"
                id="lessonPlanUpload"
                disabled={uploading}
              />
              <label htmlFor="lessonPlanUpload" className="cursor-pointer">
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-600 mb-1">Click to upload lesson plan</p>
                <p className="text-sm text-gray-500">PDF, Word, or Text files (max 10MB)</p>
              </label>
              {uploading && (
                <div className="mt-3 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  <span className="text-sm text-blue-600">Uploading...</span>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-gray-900">
                    {formData.lessonPlan?.name || 'Lesson Plan'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {formData.lessonPlan?.size && `${(formData.lessonPlan.size / 1024 / 1024).toFixed(2)} MB`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {formData.lessonPlanUrl && (
                  <a
                    href={formData.lessonPlanUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-blue-600 hover:text-blue-800"
                    title="View lesson plan"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={handleRemoveLessonPlan}
                  className="p-1 text-red-600 hover:text-red-800"
                  title="Remove lesson plan"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Content & Objectives
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Topics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topics
              </label>
              <div className="space-y-2">
                {formData.topics.map((topic, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={topic}
                      onChange={(e) => updateArrayItem('topics', index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter topic"
                    />
                    {formData.topics.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('topics', index)}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('topics')}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Topic
                </button>
              </div>
            </div>

            {/* Subtopics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subtopics
              </label>
              <div className="space-y-2">
                {formData.subtopics.map((subtopic, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={subtopic}
                      onChange={(e) => updateArrayItem('subtopics', index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter subtopic"
                    />
                    {formData.subtopics.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('subtopics', index)}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('subtopics')}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Subtopic
                </button>
              </div>
            </div>

            {/* Learning Objectives */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Learning Objectives
              </label>
              <div className="space-y-2">
                {formData.learningObjectives.map((objective, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={objective}
                      onChange={(e) => updateArrayItem('learningObjectives', index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter learning objective"
                    />
                    {formData.learningObjectives.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeArrayItem('learningObjectives', index)}
                        className="p-2 text-red-500 hover:text-red-700"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addArrayItem('learningObjectives')}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Objective
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Resources */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <File className="w-5 h-5 text-blue-600" />
            Session Resources
          </h3>
          
          <div className="space-y-4">
            {formData.resources.map((resource, index) => (
              <div key={resource.id} className="bg-white p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {resource.type === 'text' && <FileText className="w-4 h-4 text-gray-600" />}
                    {resource.type === 'file' && <File className="w-4 h-4 text-blue-600" />}
                    {resource.type === 'youtube' && <Youtube className="w-4 h-4 text-red-600" />}
                    <select
                      value={resource.type}
                      onChange={(e) => updateResource(resource.id, { 
                        type: e.target.value,
                        value: '',
                        fileName: '',
                        filePath: ''
                      })}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="text">Text Resource</option>
                      <option value="file">File Upload</option>
                      <option value="youtube">YouTube Video</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeResource(resource.id)}
                    className="p-1 text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Resource Name *
                    </label>
                    <input
                      type="text"
                      value={resource.name}
                      onChange={(e) => updateResource(resource.id, { name: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter resource name"
                    />
                  </div>

                  <div>
                    {resource.type === 'text' && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description/Notes
                        </label>
                        <textarea
                          value={resource.value}
                          onChange={(e) => updateResource(resource.id, { value: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows="2"
                          placeholder="Enter resource description"
                        />
                      </>
                    )}

                    {resource.type === 'file' && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          File Upload
                        </label>
                        {!resource.fileName ? (
                          <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.mp4,.avi,.mov,.wmv,.webm"
                              onChange={(e) => handleResourceFileUpload(e.target.files[0], resource.id)}
                              className="hidden"
                              id={`resourceFile-${resource.id}`}
                              disabled={uploading}
                            />
                            <label htmlFor={`resourceFile-${resource.id}`} className="cursor-pointer">
                              <Upload className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                              <p className="text-xs text-gray-600">Upload file</p>
                            </label>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded">
                            <span className="text-sm text-gray-700 truncate">{resource.fileName}</span>
                            <div className="flex items-center gap-1">
                              {resource.value && (
                                <a
                                  href={resource.value}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-1 text-blue-600 hover:text-blue-800"
                                >
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => updateResource(resource.id, { value: '', fileName: '', filePath: '' })}
                                className="p-1 text-red-600 hover:text-red-800"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {resource.type === 'youtube' && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          YouTube URL
                        </label>
                        <input
                          type="url"
                          value={resource.value}
                          onChange={(e) => updateResource(resource.id, { value: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                        {resource.value && !validateYouTubeUrl(resource.value) && (
                          <p className="text-xs text-red-600 mt-1">Please enter a valid YouTube URL</p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => addResource('text')}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100"
              >
                <FileText className="w-4 h-4" />
                Add Text Resource
              </button>
              <button
                type="button"
                onClick={() => addResource('file')}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100"
              >
                <File className="w-4 h-4" />
                Add File
              </button>
              <button
                type="button"
                onClick={() => addResource('youtube')}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100"
              >
                <Youtube className="w-4 h-4" />
                Add YouTube Video
              </button>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Additional Notes
          </h3>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            rows="4"
            placeholder="Any additional notes about this session..."
          />
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading || uploading}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {initialData?.id ? 'Update Session' : 'Create Session'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SessionForm;